'use client'
import { useState, useEffect } from "react"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter, DialogClose } from "../../@/components/ui/dialog"
import { Button } from "../../@/components/ui/button"
import { Input } from "../../@/components/ui/input"
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form"
import { Textarea } from "../../@/components/ui/textarea"
import { Edit, Info, HelpCircle } from "lucide-react"
import { toast } from "sonner"
import axios from "axios"
import { api_endpoint } from "@/constants/constant"
import { useAuth } from "@clerk/clerk-react"
import { ClipLoader } from "react-spinners";


const editQuizSchema = z.object({
    title: z.string().min(1, "Quiz title is required"),
    description: z.string().optional(),
    time_limit: z.coerce.number().min(0, "Time limit must be at least 0 minutes"),
    maximum_attempts: z.coerce.number().min(1, "Maximum attempts must be at least 1"),
    passmark: z.coerce.number().min(0).max(100, "Passmark must be between 0 and 100"),
})

export function EditQuizDialog({
    quizId,
    initialData = {
        title: "",
        description: "",
        time_limit: 0,
        maximum_attempts: 0,
        passmark: 0
    },
    triggerButtonProps = {},
    onQuizAdded,
    refetchTopics
}) {
    const [open, setOpen] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { getToken } = useAuth()

    const form = useForm({
        resolver: zodResolver(editQuizSchema),
        defaultValues: {
            title: initialData.title || "",
            description: initialData.description || "",
            time_limit: initialData.time_limit || 0,
            maximum_attempts: initialData.maximum_attempts || 0,
            passmark: initialData.passmark || 0,
        },
    })

    // Ensure form reflects current initialData each time dialog opens
    useEffect(() => {
        if (open) {
            form.reset({
                title: initialData.title || "",
                description: initialData.description || "",
                time_limit: initialData.time_limit || 0,
                maximum_attempts: initialData.maximum_attempts || 0,
                passmark: initialData.passmark || 0,
            })
        }
    }, [open, initialData, form])

    const onSubmit = async (values) => {
        try {
            setIsSubmitting(true)
            const token = await getToken()
            if (!token) return

            if (!quizId) {
                toast.error("Quiz ID is required for editing.")
                setIsSubmitting(false)
                return
            }

            const payload = {
                title: values.title,
                description: values.description || null,
                time_limit: values.time_limit,
                maximum_attempts: values.maximum_attempts,
                passmark: values.passmark,
            }

            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            }

            const editQuizResponse = await axios.put(api_endpoint + `/quiz/update/${quizId}`, payload, reqConfig)
            const quizData = editQuizResponse.data?.quiz ?? editQuizResponse.data

            console.log('Updated Quiz: ', quizData)
            toast.success(`Updated quiz "${quizData.title}" successfully!`)
            setOpen(false)

            if (onQuizAdded) onQuizAdded(quizData)
            if (refetchTopics) refetchTopics()

        } catch (error) {
            console.error("Error updating quiz:", error)
            toast.error("Failed to update quiz. Please try again.")
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleCancel = () => {
        // Reset to initial values instead of clearing
        form.reset({
            title: initialData.title || "",
            description: initialData.description || "",
            time_limit: initialData.time_limit || 0,
            maximum_attempts: initialData.maximum_attempts || 0,
            passmark: initialData.passmark || 0,
        })
        setOpen(false)
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className="text-blue border-blue hover:bg-blue-50"
                    {...triggerButtonProps}
                >
                    Edit Quiz
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] flex flex-col p-0">
                <DialogHeader className="px-6 py-4 border-b shrink-0">
                    <DialogTitle className="font-heading">Edit Quiz</DialogTitle>
                    <DialogDescription className="font-body">
                        Update the quiz details below. Questions can be managed separately.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-0">
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" id="edit-quiz-form">
                            {/* Quiz details section */}
                            <div className="space-y-4">
                                <FormField
                                    control={form.control}
                                    name="title"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="font-heading">Quiz Title *</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    placeholder="Intro to Machine Learning"
                                                    className="border-border-gray focus:border-red focus:ring-0 font-body"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="description"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="font-heading">
                                                Description
                                                <div className="flex items-center gap-1 text-xs text-gray-500 font-body">
                                                    <Info className="w-3 h-3" />
                                                    Markdown supported
                                                </div>
                                            </FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    {...field}
                                                    rows={3}
                                                    className="min-h-[120px] resize-none focus:border-red focus:ring-0 border-border-gray font-body"
                                                    placeholder="Describe what this quiz covers..."
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className="grid grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name="time_limit"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="font-heading">Time Limit (Minutes)*</FormLabel>
                                                <FormControl>
                                                    <Input {...field} type="number" min="0" placeholder="30" className="font-body" />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        control={form.control}
                                        name="maximum_attempts"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="font-heading">Maximum Attempts*</FormLabel>
                                                <FormControl>
                                                    <Input {...field} type="number" min="1" placeholder="3" className="font-body" />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <FormField
                                    control={form.control}
                                    name="passmark"
                                    render={({ field }) => {
                                        const value = field.value || 0
                                        const fillPercent = Math.min(Math.max(value, 0), 100) // clamp between 0–100

                                        return (
                                            <FormItem>
                                                <FormLabel className="font-heading">Passing Score (%)</FormLabel>
                                                <FormControl>
                                                    <div className="flex items-center gap-3">
                                                        {/* Slider */}
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            max={100}
                                                            value={value}
                                                            onChange={(e) => field.onChange(Number(e.target.value))}
                                                            className="w-16 border rounded px-2 py-1 text-sm text-center font-body"
                                                        />

                                                        <div className="flex-1">
                                                            <input
                                                                type="range"
                                                                min={0}
                                                                max={100}
                                                                value={value}
                                                                onChange={(e) => field.onChange(Number(e.target.value))}
                                                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer font-body"
                                                                style={{
                                                                    background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${fillPercent}%, #e5e7eb ${fillPercent}%, #e5e7eb 100%)`
                                                                }}
                                                            />
                                                        </div>

                                                        {/* Manual input field */}

                                                    </div>
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )
                                    }}
                                />
                            </div>

                            {/* Info section about questions */}
                            <div className="border border-blue rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <HelpCircle className="w-4 h-4 text-blue mt-0.5 shrink-0" />
                                    <div className="text-sm text-blue">
                                        <p className="font-medium mb-1 font-heading">Managing Questions</p>
                                        <p className="font-body">Questions for this quiz are managed separately. Use the "Add Questions" option in the quiz dropdown menu to add or modify questions.</p>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </Form>
                </div>

                <div className="flex justify-between px-6 py-4 border-t shrink-0 gap-2">
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="w-[100px] border-red border-1 hover:bg-blue hover:text-white hover:border-transparent rounded-[8px] font-heading text-red"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        type="submit"
                        form="edit-quiz-form"
                        className="bg-red text-white hover:bg-blue font-heading"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <span className="flex items-center justify-center w-full">
                                <ClipLoader size={20} color="#fff" />
                            </span>
                        ) : (
                            "Update Quiz"
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}